import {
    Button,
    ColumnLayout,
    FileUpload,
    FileUploadProps,
    Flashbar,
    FlashbarProps,
    Header,
    Link,
    ProgressBar,
    ProgressBarProps,
    SpaceBetween,
    Spinner,
    SpinnerProps,
    Table,
    TableProps,
    TextContent
} from "@amzn/awsui-components-react";
import {
    AdvancedList,
    AdvancedListItem,
    AdvancedListItemField,
    AdvancedListSearchItem,
    EntityRef,
    FieldDefinition,
    FieldDefinitionDataType,
    Pagination as SDS_Pagination,
    ReservedFieldKey
} from "@amzn/altar-sds-client";
import {
    APIOutput,
    CommonUtils,
    ContainerRef,
    FieldConfiguration,
    IAdvancedList
} from "@amzn/ask-legal-domain";
import * as React from "react";
import { Builder } from "builder-pattern";
import { CustomCommonContainerModal } from "../../container/CommonContainerModal";
import { AppContext } from "../../../setup/context";
import { fileUploadI18nStrings } from "../../../i18n/fileUploadI18nStrings";
import { AdvancedListConstants } from "../../../utils/advanced-list.constant";
import { AdvancedListPolarisFactory } from "../../../factory/polaris/advanced-list-polaris-factory";
import { SDSUtils } from "../../../utils/sds-utils";
import { ExcelFactory } from "../../../factory/file/excel-factory";
import { AdvancedListFactory } from "../../../factory/advanced-list-factory";
import { AdvancedListImportFactory } from "../../../factory/advanced-list-import-factory";
import { FileUtils } from "../../../utils/file-utils";

const SUPPORTED_FILE_EXTENSIONS = new Set([FileUtils.Extension.XLSX]);
const SUPPORTED_FILE_UPLOAD_SIZE_GB = 1;
const PREVIEW_ROW_LIMIT = 5;
const SEARCH_ITEMS_PAGE_SIZE = 100;
const SEARCH_ITEMS_MAX_PAGES = 100;
const EXISTENCE_CHECK_CHUNK_SIZE = 10;

export const ImportAdvancedListItemsModal = (props: {
    containerRef: ContainerRef;
    advancedListRef: EntityRef;
    fieldConfigurations: FieldConfiguration.Record;
    onImported: (result: {
        createdItems: AdvancedListItem[];
        updatedItems: AdvancedListItem[];
        hasFailures: boolean;
    }) => void;
    onCanceled: () => void;
}) => {
    const context = React.useContext(AppContext);
    const [spinnerProps, setSpinnerProps] = React.useState<SpinnerProps>();
    const [flashbarProps, setFlashbarProps] = React.useState<Pick<FlashbarProps, "items">>();
    const [advancedList, setAdvancedList] = React.useState<Pick<AdvancedList, "fieldDefinitions">>();
    const [fileUploadProps, setFileUploadProps] = React.useState<FileUploadProps>({
        i18nStrings: fileUploadI18nStrings,
        value: [],
        accept: [...SUPPORTED_FILE_EXTENSIONS].map((ext) => `.${ext}`).join(","),
        showFileLastModified: true,
        showFileSize: true,
        constraintText: <b>{`NOTE: File size should not exceed ${SUPPORTED_FILE_UPLOAD_SIZE_GB} GB`}</b>
    });
    const [tableProps, setTableProps] =
        React.useState<TableProps<AdvancedListImportFactory.ParsedRow> & { allItems: AdvancedListImportFactory.ParsedRow[] }>();
    const [progressBarProps, setProgressBarProps] = React.useState<ProgressBarProps>();
    const [validationTableProps, setValidationTableProps] = React.useState<TableProps<ExcelFactory.CellDataValidation>>();
    const existingItems = React.useRef<AdvancedListSearchItem[]>([]);
    const idFieldKey = React.useRef<string>();
    // Rows that failed the last upload; a retry runs only these so succeeded rows can't duplicate.
    const [retryRows, setRetryRows] = React.useState<AdvancedListImportFactory.ParsedRow[]>();

    React.useEffect(() => {
        if (!props.advancedListRef) return;
        init();
    }, [props.advancedListRef]);

    const init = async () => {
        try {
            setSpinnerProps({});
            // Load latest advanced list payload
            const loadOutput = await context.getAdvancedListAPI().load({
                entityId: props.advancedListRef.entityId,
                repositoryId: props.advancedListRef.repositoryRef.repositoryId,
                by: SDSUtils.getAmazonPersonRef(context.userId),
            });
            const output = APIOutput.fromRaw<IAdvancedList.LoadAdvancedListOutput>(loadOutput.data);
            if (output.isOk()) {
                const advancedListOutput = output.data.body;
                setAdvancedList({
                    fieldDefinitions: advancedListOutput.fieldDefinitions?.filter((def) => !def.deprecated)
                });
            } else if (output.isErr()) {
                handleError(output.err.message);
                return;
            }
        } catch (error) {
            handleError((error as Error).message);
        } finally {
            setSpinnerProps(undefined);
        }
    };

    const loadExistingItems = async (): Promise<AdvancedListSearchItem[]> => {
        const allItems: AdvancedListSearchItem[] = [];
        let totalItemsToFetch = 0;
        let pageIndex = 0;
        do {
            if (pageIndex >= SEARCH_ITEMS_MAX_PAGES) {
                // Abort: a partial snapshot would duplicate items, since unmatched rows are created.
                throw new Error(
                    `This list has too many items to import safely (over ${SEARCH_ITEMS_MAX_PAGES * SEARCH_ITEMS_PAGE_SIZE}).`
                );
            }
            const searchItemsOutput = await context.getAdvancedListAPI().searchItems({
                listEntityId: props.advancedListRef.entityId,
                listRepositoryId: props.advancedListRef.repositoryRef.repositoryId,
                pagination: Builder<SDS_Pagination>()
                    .pageSize(SEARCH_ITEMS_PAGE_SIZE)
                    .pageIndex(pageIndex + 1)
                    .build(),
                by: SDSUtils.getAmazonPersonRef(context.userId),
                containerId: props.containerRef.id
            });
            const output = APIOutput.fromRaw<IAdvancedList.SearchAdvancedListItemsOutput>(searchItemsOutput.data);
            if (output.isErr()) {
                throw new Error(output.err.message);
            }
            totalItemsToFetch = output.data.total;
            // `total` can over-count just-deleted items, so an empty page means done, not `total`.
            if (!output.data.items.length) break;
            allItems.push(...output.data.items);
            pageIndex++;
        } while (allItems.length < totalItemsToFetch);
        return allItems;
    };

    // The index lags deletes, so loadItem is the source of truth.
    // Errors read as "gone" so the row is created rather than silently dropped.
    const itemStillExists = async (extraId: string): Promise<boolean> => {
        try {
            const loadItemOutput = await context.getAdvancedListAPI().loadItem({
                entityId: props.advancedListRef.entityId,
                repositoryId: props.advancedListRef.repositoryRef.repositoryId,
                entityExtraId: extraId,
                by: SDSUtils.getAmazonPersonRef(context.userId),
                containerId: props.containerRef.id
            });
            const output = APIOutput.fromRaw<IAdvancedList.LoadAdvancedListItemOutput>(loadItemOutput.data);
            return output.isOk() && !!output.data.body;
        } catch (error) {
            return false;
        }
    };

    const findMissingItems = async (extraIds: string[]): Promise<Set<string>> => {
        const missing = new Set<string>();
        for (const batch of AdvancedListImportFactory.chunk(extraIds, EXISTENCE_CHECK_CHUNK_SIZE)) {
            const results = await Promise.all(
                batch.map(async (extraId) => ({ extraId, exists: await itemStillExists(extraId) }))
            );
            results.forEach(({ extraId, exists }) => {
                if (!exists) missing.add(extraId);
            });
        }
        return missing;
    };

    const createRow = async (row: AdvancedListImportFactory.ParsedRow): Promise<AdvancedListItem | undefined> => {
        const createItemOutput = await context.getAdvancedListAPI().createItem({
            ...toCreateItemInput(row.values),
            containerId: props.containerRef.id
        });
        const output = APIOutput.fromRaw<IAdvancedList.CreateAdvancedListItemOutput>(createItemOutput.data);
        if (output.isErr()) return undefined;
        return output.data.body;
    };

    const handleError = (message: string) => {
        setFlashbarProps({
            items: [{
                type: "error",
                content: message
            }]
        });
    };

    /**
     * Function to validate file properties
     */
    const validFile = (file: File): boolean => {
        if (!file) {
            setFileUploadProps((prev) => ({
                ...prev,
                invalid: true,
                errorText: "Please select a file",
            }));
            return false;
        }
        if (!SUPPORTED_FILE_EXTENSIONS.has(
            FileUtils.getExtension(file.name)
        )) {
            setFileUploadProps((prev) => ({
                ...prev,
                invalid: true,
                fileErrors: [`Only ${[...SUPPORTED_FILE_EXTENSIONS].map(x => `.${x}`).join(", ")} files are supported`],
            }));
            return false;
        }
        if (file.size > CommonUtils.getValueInBytes(SUPPORTED_FILE_UPLOAD_SIZE_GB, "gb")) {
            setFileUploadProps((prev) => ({
                ...prev,
                invalid: true,
                fileErrors: [`File size should not exceed ${SUPPORTED_FILE_UPLOAD_SIZE_GB} GB`],
            }));
            return false;
        }
        return true;
    };

    /**
     * Function to parse file headers
     */
    const handleFileHeader = (headersRawKeys: string[]): Record<string, FieldDefinition> | undefined => {
        let headers: Record<string, FieldDefinition> = {};
        let headersNotFound = [];
        headersRawKeys.forEach(header => {
            const fieldDefinition = advancedList?.fieldDefinitions?.find(field => field.displayName === header.trim());
            if (fieldDefinition) {
                headers[header] = fieldDefinition;
            } else {
                headersNotFound.push(header);
            }
        });
        if (headersNotFound.length) {
            handleError(`Invalid column headers found: "${headersNotFound.join(`" | "`)}"`);
            setFileUploadProps((prev) => ({
                ...prev,
                invalid: true,
                fileErrors: ["Invalid column headers detected"]
            }));
            return;
        }
        if (!Object.keys(headers).length) {
            handleError("No headers found");
            setFileUploadProps((prev) => ({
                ...prev,
                invalid: true,
                fileErrors: ["No headers found"]
            }));
            return;
        }
        return headers;
    };

    /**
     * Function to parse file data
     */
    const handleFileData = (excelData: ExcelFactory.CellData[], headers: Record<string, FieldDefinition>) => {
        const items: AdvancedListImportFactory.ParsedRow[] = [];
        const validationErrors: ExcelFactory.CellDataValidation[] = [];
        const columns = Object.keys(headers);
        const sheetFieldKeys = AdvancedListImportFactory.extractSheetFieldKeys(headers);
        idFieldKey.current = AdvancedListImportFactory.resolveIdFieldKey(headers);

        // Parse AL Item field values from every cell in each row
        excelData.forEach((rowData, row) => {
            const values: AdvancedListItemField[] = [];
            columns.forEach(column => {
                const { item, errors } = getFieldValue(headers[column], rowData[column]);
                if (errors?.length) {
                    validationErrors.push({ row, column, errors });
                }
                if (item) {
                    values.push(item);
                }
            });
            // The ID cell is for matching only; it never enters the write payload.
            const importId = AdvancedListImportFactory.extractImportId(rowData, headers);
            items.push({ values, importId, sheetFieldKeys });
        });

        if (validationErrors?.length) {
            // Validation errors found in parsed excel data
            setValidationTableProps({
                ...validationTableProps,
                columnDefinitions: [
                    {
                        id: "row",
                        header: "Row",
                        cell: item => item.row + 1 // Match excel index
                    },
                    {
                        id: "column",
                        header: "Column",
                        cell: item => item.column
                    },
                    {
                        id: "errors",
                        header: "Errors",
                        cell: item => <span style={{ color: "red" }}>{item.errors.join(", ")}</span>
                    }
                ],
                items: validationErrors
            });
            return;
        }

        setTableProps({
            ...tableProps,
            allItems: items,
            items: items.slice(0, PREVIEW_ROW_LIMIT),
            columnDefinitions: advancedList.fieldDefinitions.map(field => {
                const isIdField = field.fieldKey === ReservedFieldKey.ID
                    || field.dataType === FieldDefinitionDataType.sequence;
                return {
                    header: field.displayName,
                    cell: item => isIdField
                        ? item.importId ?? ""
                        : AdvancedListPolarisFactory.Table.renderItemDisplay(
                            item.values.find(value => value?.key === field.fieldKey)?.value,
                            field,
                            props.fieldConfigurations?.[field.fieldKey]
                        ),
                    maxWidth: "350px"
                };
            }),
        });
    };

    /**
     * Function to parse field value from cell
     */
    const getFieldValue = (field: FieldDefinition, value: any): {
        item?: AdvancedListItemField,
        errors?: string[],
    } => {
        let errors: string[];
        if (
            AdvancedListFactory.isReservedField(field.fieldKey) ||
            field.dataType === FieldDefinitionDataType.sequence
        ) return { errors };
        let restoredValue: any;
        const stringValue = String(value);
        if (value == null || !stringValue.trim().length) {
            if (field.required) {
                return {
                    errors: ["Field is required"]
                };
            }
            // return empty field value
            return { errors };
        }
        switch (field.dataType) {
            case FieldDefinitionDataType.number:
                restoredValue = value as number;
                break;
            case FieldDefinitionDataType.string:
                restoredValue = stringValue;
                break;
            case FieldDefinitionDataType.date:
                const dateValue = AdvancedListFactory.getDateinYYYYMMDD(stringValue);
                if (dateValue === "-") {
                    errors = [`Invalid date format, supported: ${AdvancedListConstants.DEFAULT_DATE_FORMAT}`];
                } else {
                    restoredValue = dateValue;
                }
                break;
            case FieldDefinitionDataType.IdentityRef:
                restoredValue = SDSUtils.getAmazonPersonRef(stringValue.trim());
                break;
            case FieldDefinitionDataType.multiIdentityRef:
                restoredValue = stringValue.trim()
                    .split(AdvancedListConstants.EXPORT_MULTI_SELECT_DELIMITER)
                    .filter(x => x.trim().length)
                    .map(SDSUtils.getAmazonPersonRef);
                break;
            case FieldDefinitionDataType.boolean:
                const extractedValue = stringValue.trim().toLowerCase();
                const truthy = ["yes", "y", "t", "1", "true"];
                const falsy = ["no", "n", "f", "0", "false"];
                if (truthy.includes(extractedValue)) {
                    restoredValue = true;
                } else if (falsy.includes(extractedValue)) {
                    restoredValue = false;
                } else {
                    errors = [`Invalid boolean value "${stringValue}"`];
                }
                break;
            case FieldDefinitionDataType.hyperlink:
                restoredValue = {
                    href: stringValue,
                    title: stringValue
                };
                break;
            case FieldDefinitionDataType.choice:
                const fieldConfig = props.fieldConfigurations?.[field.fieldKey] as FieldConfiguration.Choice;
                restoredValue = [];
                if (fieldConfig.type === FieldConfiguration.ChoiceType.SINGLE) {
                    // Handle single choice
                    const foundOption = field.choiceOptions.filter(x => x.displayValue === stringValue);
                    if (!foundOption.length) {
                        // No matching option found
                        if (field.allowFreeTextOption) {
                            // Create an ad-hoc option with provided value
                            restoredValue.push({ displayValue: stringValue });
                        } else {
                            // Add error for invalid option
                            errors = [`Option "${stringValue}" not found`];
                        }
                    } else {
                        // Return found option
                        restoredValue.push(...foundOption);
                    }
                    break;
                } else {
                    // Handle multiple choice
                    const options = stringValue
                        .split(AdvancedListConstants.EXPORT_MULTI_SELECT_DELIMITER)
                        .filter(x => x.trim().length);
                    const foundOptions = field.choiceOptions.filter(x => options.includes(x.displayValue));
                    if (foundOptions.length < options.length) {
                        // Not all options were found
                        const optionsNotFound = options.filter(x => !foundOptions.some(y => y.displayValue === x));
                        if (field.allowFreeTextOption) {
                            // Store the not found options as ad-hoc options
                            restoredValue = optionsNotFound.map(x => ({ displayValue: x }));
                        } else {
                            // Add errors for the invalid options
                            errors = optionsNotFound.map(x => `Option "${x}" not found`);
                        }
                    }
                    restoredValue = [...restoredValue, ...foundOptions];
                }
                break;
            default:
                errors = [`Unsupported field type "${field.dataType}" for column "${field.displayName}"`];
                break;
        }
        return {
            item: (restoredValue != null) && {
                key: field.fieldKey,
                type: field.dataType,
                value: restoredValue
            },
            errors
        };
    };

    const handleFileUpload = async ({ detail }) => {
        if (!detail.value?.length && spinnerProps) {
            // Do not remove file if an operation is in progress
            return;
        }
        setFileUploadProps((prev) => ({
            ...prev,
            value: detail.value,
            errorText: "",
            invalid: false,
            fileErrors: [],
            fileWarnings: [],
        }));
        setFlashbarProps(undefined);
        setTableProps(undefined);
        setValidationTableProps(undefined);
        setProgressBarProps(undefined);
        setRetryRows(undefined);
        try {
            setSpinnerProps({});
            const file = detail.value[0];
            if (!validFile(file)) return;
            const readExcelOutput = await ExcelFactory.readExcel({ file: file });
            if (!readExcelOutput.length) {
                setFileUploadProps((prev) => ({ ...prev, fileErrors: ["The excel file is empty"] }));
                handleError("Empty file uploaded");
                return;
            }
            const headers = handleFileHeader(Object.keys(readExcelOutput[0]));
            if (!headers) return;
            handleFileData(readExcelOutput.slice(0), headers);
        } catch (error) {
            handleError((error as Error).message);
        } finally {
            setSpinnerProps(undefined);
        }
    };

    const batchUpsertItems = async (items: AdvancedListImportFactory.ParsedRow[]) => {
        try {
            setSpinnerProps({});
            setProgressBarProps({
                value: 0,
                label: "Uploading...",
                description: "This may take a few minutes",
                additionalInfo: "Please do not close this window till upload is complete."
            });
            setFlashbarProps(undefined);
            // Abort if this load fails: a partial map would duplicate the whole table.
            try {
                existingItems.current = await loadExistingItems();
            } catch (error) {
                setProgressBarProps(undefined);
                handleError(
                    `Could not load the current items to match IDs against: ${(error as Error).message}. `
                        + `Nothing was imported — please retry.`
                );
                return;
            }
            const idToExtraId = AdvancedListImportFactory.buildSequenceToExtraIdMap(
                existingItems.current,
                idFieldKey.current
            );
            const itemsByExtraId = AdvancedListImportFactory.buildExtraIdToItemMap(existingItems.current);
            const rowActions = items.map(
                (item) => AdvancedListImportFactory.resolveRowAction(item.importId, idToExtraId)
            );
            const unchangedRowIndices = new Set<number>();
            const unchangedExtraIds = new Set<string>();
            rowActions.forEach((rowAction, index) => {
                if (rowAction.action !== "update") return;
                const existingItem = itemsByExtraId.get(rowAction.extraId);
                if (existingItem && AdvancedListImportFactory.isRowUnchanged(
                    items[index].values,
                    items[index].sheetFieldKeys ?? [],
                    existingItem,
                    advancedList.fieldDefinitions
                )) {
                    unchangedRowIndices.add(index);
                    unchangedExtraIds.add(rowAction.extraId);
                }
            });
            const missingExtraIds = await findMissingItems([...unchangedExtraIds]);
            const createdItems: AdvancedListItem[] = [];
            const updatedItems: AdvancedListItem[] = [];
            const failedItemsIndices: number[] = [];
            let createdCount = 0;
            let updatedCount = 0;
            let unchangedCount = 0;
            let unmatchedCreatedCount = 0;

            for await (const [index, item] of items.entries()) {
                const rowAction = rowActions[index];
                try {
                    let createInstead = false;
                    if (rowAction.action === "update") {
                        if (unchangedRowIndices.has(index)) {
                            if (missingExtraIds.has(rowAction.extraId)) {
                                createInstead = true;
                            } else {
                                unchangedCount++;
                            }
                        } else {
                            let updateFailed = false;
                            try {
                                const updateItemOutput = await context.getAdvancedListAPI().updateItem({
                                    ...toUpdateItemInput(item, rowAction.extraId),
                                    containerId: props.containerRef.id
                                });
                                const output = APIOutput.fromRaw<IAdvancedList.UpdateAdvancedListItemOutput>(updateItemOutput.data);
                                if (output.isErr()) {
                                    updateFailed = true;
                                } else {
                                    updatedCount++;
                                    if (output.data.body) updatedItems.push(output.data.body);
                                }
                            } catch (error) {
                                updateFailed = true;
                            }
                            if (updateFailed) {
                                createInstead = !(await itemStillExists(rowAction.extraId));
                                if (!createInstead) failedItemsIndices.push(index);
                            }
                        }
                    }
                    if (rowAction.action === "create" || createInstead) {
                        const createdItem = await createRow(item);
                        if (!createdItem) {
                            failedItemsIndices.push(index);
                        } else {
                            createdCount++;
                            if (rowAction.action === "create" ? !!rowAction.unmatchedImportId : !!item.importId) {
                                unmatchedCreatedCount++;
                            }
                            createdItems.push(createdItem);
                        }
                    }
                } catch (error) {
                    failedItemsIndices.push(index);
                }
                setProgressBarProps({
                    ...progressBarProps,
                    label: "Uploading...",
                    description: "This may take a few minutes",
                    additionalInfo: "Please do not close this window till upload is complete.",
                    value: calculateProgress(
                        items.length,
                        createdCount + updatedCount + unchangedCount + failedItemsIndices.length
                    ),
                });
            }
            const failedCount = failedItemsIndices.length;
            const succeededCount = createdCount + updatedCount;
            setRetryRows(failedCount
                ? items.filter((_, index) => failedItemsIndices.includes(index))
                : undefined);
            const summaryParts: string[] = [];
            if (createdCount) summaryParts.push(`${createdCount} created`);
            if (updatedCount) summaryParts.push(`${updatedCount} updated`);
            if (unchangedCount) summaryParts.push(`${unchangedCount} unchanged`);
            if (failedCount) summaryParts.push(`${failedCount} failed`);
            const summary = summaryParts.join(", ");
            const unmatchedNote = unmatchedCreatedCount
                ? ` ${unmatchedCreatedCount} row(s) had IDs that didn't match any existing item and `
                    + `were imported as new items (new IDs were assigned).`
                : "";
            if (failedCount) {
                setFlashbarProps({
                    items: [{
                        type: succeededCount > 0 ? "warning" : "error",
                        content: (succeededCount > 0
                            ? `Import finished with some failures: ${summary}. Download the failed rows below to review.`
                            : `Import failed: ${summary}. Download the failed rows below to review.`) + unmatchedNote
                    }]
                });
                setProgressBarProps({
                    ...progressBarProps,
                    status: "error",
                    resultText: summary,
                    label: "Result",
                    description: (succeededCount > 0
                        ? "Some rows succeeded and some failed. Download the failed rows to see what went wrong."
                        : "No rows were imported. Download the failed rows to see what went wrong.") + unmatchedNote,
                    resultButtonText: "Download failed items",
                    onResultButtonClick: () => {
                        // Re-injects the typed ID so the download is diagnosable and re-importable.
                        exportItems(
                            AdvancedListImportFactory.toExportRows(
                                items.filter((_, index) => failedItemsIndices.includes(index))
                            ),
                            "Failed_Import"
                        );
                    }
                });
            } else {
                setProgressBarProps({
                    ...progressBarProps,
                    status: "success",
                    resultText: summary,
                    label: "Result",
                    description: (succeededCount > 0
                        ? "All items imported successfully."
                        : "Every row already matches the table, so nothing needed updating.") + unmatchedNote,
                });
            }
            if (props.onImported && (succeededCount > 0 || !failedCount)) {
                props.onImported({ createdItems, updatedItems, hasFailures: failedCount > 0 });
            }
        } catch (error) {
            handleError((error as Error).message);
        } finally {
            setSpinnerProps(undefined);
        }
    };

    const toCreateItemInput = (values: AdvancedListItemField[]): IAdvancedList.CreateAdvancedListItemInput => {
        return Builder<IAdvancedList.CreateAdvancedListItemInput>()
            .entityId(props.advancedListRef.entityId)
            .repositoryId(props.advancedListRef.repositoryRef.repositoryId)
            .by(SDSUtils.getAmazonPersonRef(context.userId))
            .values(values)
            .build();
    };

    const toUpdateItemInput = (
        row: AdvancedListImportFactory.ParsedRow,
        entityExtraId: string
    ): IAdvancedList.UpdateAdvancedListItemInput => {
        const removeValues = AdvancedListImportFactory.computeRemoveValues(
            advancedList.fieldDefinitions,
            row.values,
            row.sheetFieldKeys ?? []
        );
        return Builder<IAdvancedList.UpdateAdvancedListItemInput>()
            .entityId(props.advancedListRef.entityId)
            .repositoryId(props.advancedListRef.repositoryRef.repositoryId)
            .entityExtraId(entityExtraId)
            .by(SDSUtils.getAmazonPersonRef(context.userId))
            .addOrUpdateValues(row.values)
            .removeValues(removeValues)
            .build();
    };

    const calculateProgress = (total: number, uploaded: number) => {
        return Math.ceil((uploaded / total) * 100);
    };

    const exportItems = (items: Pick<AdvancedListSearchItem, "values">[], filePrefix?: string) => {
        AdvancedListFactory.exportItemsToFile({
            fieldConfigurationRecord: props.fieldConfigurations,
            fieldDefinitions: advancedList.fieldDefinitions,
            items: items,
            filePrefix: filePrefix
        });
    };

    const fileUploadInstructions = <TextContent>
        <strong>Steps to upload a file:</strong>
        <br/><strong>&nbsp;&nbsp;Step 1 : </strong>Click <Link onClick={() => {
            exportItems([], "Template");
        }} external>here</Link> to download the file template
        <br/><strong>&nbsp;&nbsp;Step 2 : </strong>Fill out the template file from step 1 above and then drop the file here or click below to browse.
    </TextContent>;

    const component = <>
        <SpaceBetween size="m">
            <ColumnLayout columns={2} borders="vertical">
                <SpaceBetween size="s">
                    {fileUploadInstructions}
                    <FileUpload {...fileUploadProps} onChange={handleFileUpload} />
                </SpaceBetween>
                {progressBarProps && <ProgressBar
                    {...progressBarProps}
                />}
            </ColumnLayout>
            <hr />
            {!!validationTableProps?.items.length && <Table
                {...validationTableProps}
                header={<Header
                    counter={`(${validationTableProps.items.length} errors found)`}
                >Validation Errors</Header>}
            />}
            {!!tableProps?.items.length && <Table
                {...tableProps}
                header={<Header
                    variant="h2"
                    counter={`${tableProps.allItems.length} items found`}
                    actions={
                        <Button
                            variant="primary"
                            disabled={!tableProps.items.length}
                            loading={!!spinnerProps}
                            onClick={() => {
                                batchUpsertItems(retryRows ?? tableProps.allItems);
                            }}
                        >{retryRows ? `Retry ${retryRows.length} Failed Items` : "Upload All Items"}</Button>
                    }
                >Preview</Header>}
            />}
        </SpaceBetween>
    </>;

    return <CustomCommonContainerModal
        header="Import Items from Excel"
        child={<>
            <SpaceBetween size="m">
                {spinnerProps && <Spinner {...spinnerProps} />}
                {flashbarProps && <Flashbar {...flashbarProps} />}
                {advancedList && component}
            </SpaceBetween>
        </>}
        loading={!!spinnerProps}
        disabled={!!spinnerProps || progressBarProps?.status === "error"}
        onCancel={() => props.onCanceled()}
        size="xl"
    />;
};